import { Info } from "lucide-react";
import { useEffect, useState } from "react";
import { ICON_STROKE } from "../../components/icons/icon-tokens";

export const DISCLAIMER_STORAGE_KEY = "chatv2_disclaimer_acknowledged_v1";

/**
 * Lightweight hook for parents that need to render related chrome
 * (dividers, gaps) only while the disclaimer banner itself is visible.
 * Mirrors the storage check inside DisclaimerBanner — they stay in sync
 * because the banner writes to the same key when "Got it" is clicked,
 * and we listen for `storage` events to react in the same tab as well
 * (via a custom `disclaimer-acknowledged` event the banner dispatches).
 */
export function useDisclaimerVisible(): boolean {
  const [visible, setVisible] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return !localStorage.getItem(DISCLAIMER_STORAGE_KEY);
  });

  useEffect(() => {
    const sync = () => setVisible(!localStorage.getItem(DISCLAIMER_STORAGE_KEY));
    window.addEventListener("storage", sync);
    window.addEventListener("disclaimer-acknowledged", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("disclaimer-acknowledged", sync);
    };
  }, []);

  return visible;
}

interface DisclaimerBannerProps {
  /** Callback when banner is dismissed */
  onDismiss?: () => void;
  /**
   * Expanded variant uses a glassy white tint + backdrop-blur to match
   * `.a-main-notice .a-notice` in the mock (the notice sits on the cream
   * wash, not inside a divider sandwich). Compact uses `--d-cream-soft`.
   */
  variant?: "compact" | "expanded";
}

/**
 * Disclaimer Banner Component
 *
 * Shows a first-time use disclaimer for Chat v2 that users must acknowledge.
 * - Persists acknowledgment in localStorage with version key
 * - Styled to match app's design system (info colors, semantic typography)
 * - Non-blocking: appears between header and messages
 * - Dismissible via "Got it" button
 *
 * @example
 * ```tsx
 * <DisclaimerBanner onDismiss={() => console.log('User acknowledged')} />
 * ```
 */
export function DisclaimerBanner({ onDismiss, variant = "compact" }: DisclaimerBannerProps) {
  const isExpanded = variant === "expanded";
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already acknowledged the disclaimer
    const hasAcknowledged = localStorage.getItem(DISCLAIMER_STORAGE_KEY);
    setIsVisible(!hasAcknowledged);
  }, []);

  const handleConfirm = () => {
    // Persist acknowledgment to localStorage
    localStorage.setItem(DISCLAIMER_STORAGE_KEY, "true");
    setIsVisible(false);
    // Broadcast so siblings (e.g. dividers around this notice) can also
    // unmount themselves in the same render tick, without polling.
    window.dispatchEvent(new Event("disclaimer-acknowledged"));
    onDismiss?.();
  };

  // Don't render if already acknowledged
  if (!isVisible) return null;

  // `.a-notice` card from the Claude-Design "Autumn Assistant" mock:
  // cream-soft tinted card with a round glyph chip on the left, ink body
  // copy, and a pill-shaped dark CTA. Lives between the greeting block
  // and the message list — sits on the panel's ambient wash, no harsh
  // surface-secondary stripe across the whole panel.
  return (
    <div className="w-full px-[22px] py-[14px] flex-shrink-0">
      <div
        className="flex items-start gap-3"
        style={{
          padding: "12px 14px",
          background: isExpanded
            ? "rgba(255,255,255,0.7)"
            : "var(--d-cream-soft)",
          border: "1px solid var(--d-rule-soft)",
          borderRadius: 12,
          boxShadow: "var(--d-shadow-card)",
          backdropFilter: isExpanded ? "blur(8px)" : "blur(0px)",
          WebkitBackdropFilter: isExpanded ? "blur(8px)" : "blur(0px)",
          transition:
            "background 420ms cubic-bezier(0.22,1,0.36,1), backdrop-filter 420ms cubic-bezier(0.22,1,0.36,1), -webkit-backdrop-filter 420ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {/* Round glyph chip — surface bg with hairline rule, ink-muted icon */}
        <div
          className="flex-shrink-0 inline-flex items-center justify-center"
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "var(--d-surface)",
            border: "1px solid var(--d-rule)",
            color: "var(--d-ink-muted)",
          }}
          aria-hidden="true"
        >
          <Info className="w-[14px] h-[14px]" strokeWidth={ICON_STROKE} />
        </div>

        {/* Body + dark pill CTA */}
        <div className="flex-1 min-w-0">
          <p
            className="mb-2"
            style={{
              fontFamily: "var(--d-font-body)",
              fontSize: 12.5,
              lineHeight: 1.45,
              color: "var(--d-ink)",
            }}
          >
            I'm Autumn's new AI Agent. I'm still learning and might make
            mistakes.
          </p>

          <button
            onClick={handleConfirm}
            type="button"
            className="transition-colors"
            style={{
              padding: "5px 12px",
              background: "var(--d-ink)",
              color: "#fff",
              border: 0,
              // Standard button radius (8px) instead of a full 999 pill.
              borderRadius: "var(--d-r-lg)",
              fontFamily: "var(--d-font-sans)",
              fontSize: 11.5,
              fontWeight: 500,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--d-accent-deep)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--d-ink)";
            }}
            aria-label="Acknowledge disclaimer"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
